import { stringify } from "query-string";
import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

import {
  TeamPopulated, DocumentInput, GetTeamListRequest, TeamSecondaryLogoColorDuoEntry,
  TeamAdsWithId, GetTeamAdsByTeamRequest, UpdateTeamAdsRequest,
  AddTeamPopupsRequest,
  GetTeamPopupsByTeamRequest,
  TeamPopupConfigPopulated,
  UpdateTeamPopupsItemsRequest,
  TicketTemplatePlacement,
} from "../../shared/interfaces";
import { PATH } from "../../shared/constants";

export const getTeamListRequest = async (request: GetTeamListRequest): Promise<TeamPopulated[]> => {
  const stringified = (request.league || request.sport)
    ? stringify(request, { skipNull: true })
    : "";

  try {
    const url = stringified
      ? `${PATH.API}/${PATH.TEAM}?${stringified}`
      : `${PATH.API}/${PATH.TEAM}`;
    const response = await instance.get(url);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamByIdRequest = async (teamId: string): Promise<TeamPopulated> => {
  try {
    const response = await instance.get(`${PATH.API}/${PATH.TEAM}/${teamId}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamAddsByIdRequest = async (_id: string): Promise<TeamAdsWithId> => {
  try {
    const response = await instance.get(`${PATH.API}/${PATH.TEAM_ADS}/${_id}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamAddsByTeamIdRequest = async (
  request: GetTeamAdsByTeamRequest,
): Promise<TeamAdsWithId> => {
  try {
    const stringified = stringify(request, {
      skipNull: true,
    });
    const response = await instance.get(`${PATH.API}/${PATH.TEAM_ADS_FOR_TEAM}?${stringified}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addTeamRequest = async (request: FormData): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const uploadTeamSecondaryLogoColorDuoRequest = async (
  teamId: string,
  file: File,
  name: string,
  primaryColor: string,
): Promise<TeamSecondaryLogoColorDuoEntry> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("primaryColor", primaryColor);
    const response = await instance.post<{
      success: boolean;
      entry: TeamSecondaryLogoColorDuoEntry;
    }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/secondary-logo`,
      formData,
    );
    return response.data.entry;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateTeamRequest = async (
  request: FormData, teamId: string,
): Promise<DocumentInput> => {
  try {
    const response = await instance.put<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const createUserFlowPreviewRequest = async (
  teamId: string,
  previewHostname: string,
  email: string,
): Promise<TeamPopulated> => {
  try {
    const response = await instance.post<TeamPopulated>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/user-flow/preview`,
      { previewHostname, email },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const publishUserFlowDraftRequest = async (
  teamId: string,
  email: string,
): Promise<TeamPopulated> => {
  try {
    const response = await instance.post<TeamPopulated>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/user-flow/publish`,
      { email },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addTeamAdsRequest = async (request: UpdateTeamAdsRequest): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_ADS}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateTeamAdsRequest = async (
  request: FormData, teamAdsId: string,
): Promise<DocumentInput> => {
  try {
    const response = await instance.put<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_ADS}/${teamAdsId}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamPopupsByTeamIdRequest = async (
  request: GetTeamPopupsByTeamRequest,
): Promise<TeamPopupConfigPopulated | null> => {
  try {
    const stringified = stringify(request, { skipNull: true });
    const response = await instance.get(
      `${PATH.API}/${PATH.TEAM_POPUPS_FOR_TEAM}?${stringified}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addTeamPopupsRequest = async (
  request: AddTeamPopupsRequest,
): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_POPUPS}`,
      request,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateTeamPopupsItemsRequest = async (
  configId: string,
  body: UpdateTeamPopupsItemsRequest,
): Promise<TeamPopupConfigPopulated> => {
  try {
    const response = await instance.put<TeamPopupConfigPopulated>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_POPUPS}/${configId}/items`,
      body,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateTeamPopupImageRequest = async (
  request: FormData,
  configId: string,
): Promise<{ success: boolean; uploadURL?: string }> => {
  try {
    const response = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_POPUPS}/${configId}`,
      request,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteTeamRequest = async (teamId: string): Promise<boolean> => {
  try {
    await instance.delete<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}`);

    return true;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface ClientServiceImage {
  key: string;
  url: string;
}

export interface TeamClientServiceData {
  useCaseImages: ClientServiceImage[];
  promotionImages: ClientServiceImage[];
  socialMediaPostsImages: ClientServiceImage[];
}

export const getTeamClientServiceRequest = async (
  teamId: string,
): Promise<TeamClientServiceData> => {
  try {
    const response = await instance.get<TeamClientServiceData>(
      `${PATH.API}/${PATH.TEAM_CLIENT_SERVICE}/${teamId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const uploadTeamClientServiceImageRequest = async (
  teamId: string,
  type: "useCase" | "promotion" | "socialMediaPosts",
  filename: string,
  imageMimeType: string,
  teamName: string,
): Promise<{ uploadURL: string; uploadKey: string }> => {
  try {
    const response = await instance.post<{ uploadURL: string; uploadKey: string }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_CLIENT_SERVICE}/${teamId}/upload`,
      { type, filename, imageMimeType, teamName },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteTeamClientServiceImageRequest = async (
  teamId: string,
  type: "useCase" | "promotion" | "socialMediaPosts",
  key: string,
): Promise<void> => {
  try {
    await instance.delete(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM_CLIENT_SERVICE}/${teamId}/image`,
      { params: { type, key } },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamDefaultTicketPlacementRequest = async (
  teamId: string,
): Promise<TicketTemplatePlacement | null> => {
  try {
    const response = await instance.get<{ placement: TicketTemplatePlacement | null }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/default-ticket-placement`,
    );
    return response.data.placement;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const setTeamDefaultTicketPlacementRequest = async (
  teamId: string,
  placement: TicketTemplatePlacement,
): Promise<TicketTemplatePlacement> => {
  try {
    const response = await instance.put<{ success: boolean; placement: TicketTemplatePlacement }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/default-ticket-placement`,
      placement,
    );
    return response.data.placement;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export type PublicEngagementLinkAction =
  | "generate"
  | "regenerate"
  | "disable"
  | "refresh"
  | "set_refresh_mode";

export type PublicEngagementRefreshMode = "auto" | "manual";

type ManagePublicEngagementLinkResponse = {
  success: boolean;
  publicEngagementToken: string | null;
  refreshMode: PublicEngagementRefreshMode;
  refreshAt: number;
};

export const manageTeamPublicEngagementLinkRequest = async (
  teamId: string,
  action: PublicEngagementLinkAction,
  options?: { refreshMode?: PublicEngagementRefreshMode },
): Promise<{
  publicEngagementToken: string | null;
  refreshMode: PublicEngagementRefreshMode;
  refreshAt: number;
}> => {
  try {
    const response = await instance.post<ManagePublicEngagementLinkResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/public-engagement-link`,
      { action, refreshMode: options?.refreshMode },
    );
    return {
      publicEngagementToken: response.data.publicEngagementToken ?? null,
      refreshMode: response.data.refreshMode ?? "auto",
      refreshAt: response.data.refreshAt ?? 0,
    };
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface SeasonLongOptOutRow {
  _id: string;
  section: string;
  row: string;
  seat: string;
  email?: string;
  fanId?: string;
  birthDate?: string;
}

export interface SeasonLongOptOutsPayload {
  seasons: { _id: string; name: string }[];
  selectedSeasonId: string | null;
  referenceGameId: string | null;
  optOuts: SeasonLongOptOutRow[];
}

export const getSeasonLongOptOutsRequest = async (
  teamId: string,
  seasonId?: string,
): Promise<SeasonLongOptOutsPayload> => {
  try {
    const qs = seasonId
      ? `?seasonId=${encodeURIComponent(seasonId)}`
      : "";
    const response = await instance.get<SeasonLongOptOutsPayload>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/${PATH.SEASON_OPT_OUTS}${qs}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addSeasonLongOptOutRequest = async (
  teamId: string,
  body: {
    seasonId?: string;
    section: string;
    row: string;
    seat: string;
    email?: string;
  },
): Promise<SeasonLongOptOutRow> => {
  try {
    const response = await instance.post<SeasonLongOptOutRow>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/${PATH.SEASON_OPT_OUTS}`,
      body,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const uploadSeasonLongOptOutsRequest = async (
  teamId: string,
  body: { seasonId?: string; csv: string },
): Promise<{ added: number; skipped: number }> => {
  try {
    const response = await instance.post<{ added: number; skipped: number }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/${PATH.SEASON_OPT_OUTS}/upload`,
      body,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteSeasonLongOptOutRequest = async (
  teamId: string,
  optOutId: string,
): Promise<void> => {
  try {
    await instance.delete(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.TEAM}/${teamId}/${PATH.SEASON_OPT_OUTS}/${optOutId}`,
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
